import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import { Apps } from 'services/apps';
import './PricingPage.css';

// Build the plan cards. `rates` (fetched from GET /apps/pricing, which serializes
// the backend's config/pricing-rates.js) fills the Cloud column's LIVE usage
// numbers — ONE source of truth, so the page can never quote a price billing
// doesn't charge.
//
// ⚠️ There is deliberately NO hardcoded fallback PRICE. A baked-in number is a
// second place that must agree with pricing-rates.js, with nothing to catch the
// drift — and it drifted: the old fallback said "from $0.001 / min" against a
// real rate of $0.018, an 18× understatement that shipped from 2026-06-23
// (e336c83) until it was found during Slack Marketplace review, where quoting an
// inaccurate price is itself a rejection reason. When the fetch fails we say the
// pricing MODEL (which is stable and true) and omit the figure. Never reintroduce
// a literal price here; add it to pricing-rates.js and let it flow through.
const RATE_PENDING = 'Usage-based';
const buildPlans = (rates = null) => {
  const r = rates || {};
  const agentMin = r.agentRunUsdPerMin?.warm;
  const appMo = r.managedAppUsdPerMonth?.light;
  const egressMo = r.dedicatedEgressUsdPerMonth;
  const agentRunVal = agentMin != null ? `from $${agentMin} / min` : RATE_PENDING;
  const appHostVal = appMo != null ? `from $${appMo} / mo` : RATE_PENDING;
  const egressVal = egressMo != null ? `+ $${egressMo} / mo` : RATE_PENDING;
  return [
  {
    id: 'open_source',
    name: 'Open Source',
    price: 'Free',
    priceNote: 'forever',
    desc: 'Get started with Teamy CLI — bring your own AI tokens',
    cta: 'Get started',
    ctaStyle: 'outline',
    sections: [
      {
        label: 'Included',
        rows: [
          { label: 'Teamy CLI runner', value: 'Included' },
          { label: 'Playwright script generation', value: 'Included' },
          { label: 'Local test execution', value: 'Unlimited' },
          { label: 'Your own AI tokens (Claude / Codex / Gemini)', value: 'Required' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Community / GitHub Issues', value: 'Included' },
        ],
      },
    ],
  },
  {
    id: 'cloud',
    name: 'Cloud',
    price: 'Pay as you go',
    priceNote: '',
    desc: 'Run agents and apps in the cloud — pay only for what runs. No monthly fee.',
    highlight: true,
    cta: 'Start building',
    ctaStyle: 'solid',
    sections: [
      {
        label: 'Everything in Open Source, plus',
        rows: [
          { label: 'Cloud agent runs', value: agentRunVal },
          { label: 'In-process runs', value: 'Free' },
          { label: 'Managed app hosting', value: appHostVal },
          { label: 'Storage & bandwidth', value: 'Metered' },
        ],
      },
      {
        label: 'Billing',
        rows: [
          { label: 'Pay only for what runs', value: 'Usage-based credits' },
          { label: 'Your own AI keys (Claude / Codex / Gemini)', value: 'Required' },
        ],
      },
      {
        label: 'Integrations',
        rows: [
          { label: 'Jira / GitLab / GitHub', value: 'Included' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Email support', value: 'Included' },
        ],
      },
      {
        label: 'Add-ons (optional)',
        rows: [
          { label: 'Dedicated egress IP', value: egressVal },
        ],
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    priceNote: '',
    desc: 'Volume usage rates with dedicated support, SSO, and custom integrations',
    cta: 'Contact',
    ctaStyle: 'outline',
    ctaHref: 'mailto:support@teamy.town',
    sections: [
      {
        label: 'Everything in Cloud, plus',
        rows: [
          { label: 'Volume usage rates', value: 'Available' },
          { label: 'SSO / SAML', value: 'Available' },
          { label: 'Bring your own cloud (BYOC)', value: 'Available' },
        ],
      },
      {
        label: 'Integrations',
        rows: [
          { label: 'All standard integrations', value: 'Included' },
          { label: 'Custom integrations', value: 'Available' },
        ],
      },
      {
        label: 'Support',
        rows: [
          { label: 'Priority support + SLA', value: 'Included' },
        ],
      },
      {
        label: 'Add-ons (optional)',
        rows: [
          { label: 'Dedicated egress IP', value: egressVal },
        ],
      },
    ],
  },
  ];
};

const tools = [
  {
    title: 'Ticket analysis',
    desc: 'AI-powered analysis of tickets to generate implementation plans, test cases, and code review criteria automatically.',
  },
  {
    title: 'Code implementation',
    desc: 'Automated code generation and modification based on ticket requirements with built-in best practices.',
  },
  {
    title: 'Integration hub',
    desc: 'Connect with Jira, Linear, GitLab, GitHub, and more to streamline your development workflow.',
  },
  {
    title: 'Dedicated egress IP',
    // No price in this prose — the rate is quoted once, in the plan table, from
    // the live rates. A second copy here drifted to $50 while billing said $51.
    desc: 'Optional Cloud add-on. Get a static outbound IP for your account so you can allowlist Teamy on corporate GitLab / GitHub firewalls. Billed monthly, prorated — see the Cloud plan above for the current rate.',
  },
];

// ── Self-hosted licences ─────────────────────────────────────────────────────
// Prices and agent caps come from GET /apps/pricing `selfHostedPlans` (backend
// config/selfhost-plans.js — the same numbers checkout charges and the licence
// enforces). Like the Cloud column, this page quotes no figure of its own: until
// the plans load, the cards say the model and no price.
const SELF_HOSTED_COPY = {
  free: { desc: 'Try the whole agent team on your own machine.', cta: 'Download', perks: ['All marketplace agents', 'Runs on your machine', 'Community support'] },
  team: { desc: 'For a team putting agents to work every day.', cta: 'Buy Pro', perks: ['Everything in Free', 'Email support'] },
  business: { desc: 'For a company running several teams of agents.', cta: 'Buy Max', perks: ['Everything in Pro', 'Priority email support'], highlight: true },
};
const agentsLabel = (maxAgents) => (maxAgents == null ? 'Unlimited agents' : `Up to ${maxAgents} agents`);
const priceLabel = (plan, period) => {
  if (!plan.monthlyUsd) return { value: '$0', note: 'forever' };
  return period === 'yearly'
    ? { value: `$${plan.yearlyUsd.toLocaleString('en-US')}`, note: '/ year' }
    : { value: `$${plan.monthlyUsd.toLocaleString('en-US')}`, note: '/ month' };
};
// Paid plans: sign in first, then checkout. `box` (the machine's installation id,
// passed by the dashboard's "Upgrade license" link) rides along so the paid
// licence lands on that machine by itself.
const checkoutHref = (planId, period, box) => {
  const q = new URLSearchParams({ plan: planId, period });
  if (box) q.set('box', box);
  return `/signin?returnTo=${encodeURIComponent(`/self-hosted/checkout?${q.toString()}`)}`;
};

const SelfHostedSection = ({ plans }) => {
  const [period, setPeriod] = useState('monthly');
  const box = (() => {
    try { const v = new URLSearchParams(window.location.search).get('box') || ''; return /^[A-Za-z0-9-]{8,64}$/.test(v) ? v : ''; } catch { return ''; }
  })();
  const cards = (plans || [{ id: 'free' }, { id: 'team' }, { id: 'business' }]).filter((p) => SELF_HOSTED_COPY[p.id]);
  return (
    <div className="pricing-content pricing-selfhosted" id="self-hosted">
      <div className="pricing-models-header reveal">
        <div>
          <h2>Self-hosted</h2>
          <p className="pricing-selfhosted-sub">One price per machine for your whole team. No usage bill — your data never leaves your machine.</p>
        </div>
        <div className="pricing-period">
          <span>Yearly <span className="pricing-period-save">2 months free</span></span>
          <button
            type="button"
            className={`pricing-toggle${period === 'yearly' ? ' pricing-toggle--active' : ''}`}
            aria-pressed={period === 'yearly'}
            aria-label="Bill yearly"
            onClick={() => setPeriod(period === 'yearly' ? 'monthly' : 'yearly')}
          />
        </div>
      </div>
      <div className="pricing-cards">
        {cards.map((plan, i) => {
          const copy = SELF_HOSTED_COPY[plan.id];
          const price = plans ? priceLabel(plan, period) : { value: plan.id === 'free' ? '$0' : 'Per machine', note: '' };
          const href = plan.id === 'free' ? '/download' : checkoutHref(plan.id, period, box);
          return (
            <div key={plan.id} className={`pricing-card${copy.highlight ? ' pricing-card--highlight' : ''} reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
              <div>
                <h3 className="pricing-card-title">{plan.name || plan.id}</h3>
                <div className="pricing-card-price">
                  <span className="pricing-card-price-value">{price.value}</span>
                  {price.note && <span className="pricing-card-price-note">{price.note}</span>}
                </div>
                <p className="pricing-card-desc">{copy.desc}</p>
              </div>
              <a href={href} className={`pricing-card-cta pricing-card-cta--${copy.highlight ? 'solid' : 'outline'}`}>{copy.cta}</a>
              <div className="pricing-table-section">
                <div className="pricing-row"><span className="pricing-row-label">Agents you can deploy</span><span className="pricing-row-value">{plans ? agentsLabel(plan.maxAgents) : '—'}</span></div>
                {copy.perks.map((perk) => (
                  <div key={perk} className="pricing-row"><span className="pricing-row-label">{perk}</span><span className="pricing-row-value">Included</span></div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="pricing-selfhosted-more reveal">Need it on several machines? <a href="mailto:support@teamy.town">Contact us</a></p>
    </div>
  );
};

const PricingPage = () => {

  // Live rates from the backend (config/pricing-rates.js via GET /apps/pricing).
  // null until loaded / on failure → buildPlans renders the pricing MODEL without
  // a figure (see RATE_PENDING). The page always renders; it just never invents
  // a price.
  const [rates, setRates] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Apps.getPricing()
      .then((data) => { if (!cancelled) setRates(data); })
      .catch(() => { /* keep fallback rates — never block the page */ });
    return () => { cancelled = true; };
  }, []);
  const plans = buildPlans(rates);
  // One product at a time: Self-hosted (the default) or Cloud. The hash keeps a
  // link to either (#self-hosted from the dashboard's Upgrade link, #cloud).
  const [tab, setTab] = useState(() => (typeof window !== 'undefined' && window.location.hash === '#cloud' ? 'cloud' : 'self-hosted'));
  const pageRef = useScrollReveal({ key: tab });
  const pickTab = (next) => {
    setTab(next);
    try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${next}`); } catch { /* keep the tab anyway */ }
  };

  return (
    <>
      <Helmet>
        <title>Pricing - Teamy</title>
        <meta name="description" content="Teamy Pricing - AI-powered software development platform pricing plans for teams of all sizes." />
      </Helmet>
      <div className="public-page fade-in" ref={pageRef}>
        <PublicNav />

        <div className="pricing-top">
        {/* Hero */}
        <div className="pricing-hero">
          <h1 className="reveal">Pricing</h1>
        </div>

        <div className="pricing-tabs" role="tablist" aria-label="Product">
          <div className="pricing-tabs-inner">
            {[['self-hosted', 'Self-hosted'], ['cloud', 'Cloud']].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id}
                className={`pricing-tab${tab === id ? ' pricing-tab--active' : ''}`} onClick={() => pickTab(id)}>{label}</button>
            ))}
          </div>
        </div>

        {tab === 'self-hosted' && (
          <SelfHostedSection plans={rates && Array.isArray(rates.selfHostedPlans) ? rates.selfHostedPlans : null} />
        )}

        {/* Plans */}
        {tab === 'cloud' && (
        <div className="pricing-content">
          <div className="pricing-models-header reveal">
            <h2>Plans</h2>
            <div className="pricing-models-header-actions">
              <a href="mailto:support@teamy.town" className="pricing-btn pricing-btn--outline">Contact</a>
              <a href="/connect" className="pricing-btn pricing-btn--solid">Start building</a>
            </div>
          </div>

          <div className="pricing-cards">
            {plans.map((plan, i) => (
              <div key={plan.name} className={`pricing-card${plan.highlight ? ' pricing-card--highlight' : ''} reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
                <div>
                  <h3 className="pricing-card-title">{plan.name}</h3>
                  <div className="pricing-card-price">
                    <span className="pricing-card-price-value">{plan.price}</span>
                    {plan.priceNote && <span className="pricing-card-price-note">{plan.priceNote}</span>}
                  </div>
                  <p className="pricing-card-desc">{plan.desc}</p>
                </div>
                <a
                  href={plan.ctaHref || `/connect?returnTo=/billing&plan=${plan.id}`}
                  className={`pricing-card-cta pricing-card-cta--${plan.ctaStyle}`}
                >
                  {plan.cta}
                </a>
                {plan.sections.map((section) => (
                  <div key={section.label} className="pricing-table-section">
                    <div className="pricing-table-label">{section.label}</div>
                    {section.rows.map((row) => (
                      <div key={row.label} className="pricing-row">
                        <span className="pricing-row-label">{row.label}</span>
                        <span className="pricing-row-value">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>


          {/* Footnotes */}
          <div className="pricing-footnotes reveal">
            <p>Open source CLI and Playwright generation are free forever — bring your own AI tokens.</p>
            <p>Cloud and Enterprise include cloud infrastructure, automatic updates, and data encryption at rest.</p>
            <a href="mailto:support@teamy.town" className="pricing-footnotes-btn">Contact us for Enterprise pricing</a>
          </div>
        </div>
        )}
        </div>

        {/* Tools section (Cloud add-ons) */}
        {tab === 'cloud' && (
        <div className="pricing-tools">
          <div className="pricing-tools-inner">
            <h2 className="reveal">Pricing for tools</h2>
            <p className="pricing-tools-subtitle reveal reveal-delay-1">Get even more out of Teamy with advanced features and capabilities.</p>
            <div className="pricing-tools-cards">
              {tools.map((tool, i) => (
                <div key={tool.title} className={`pricing-tool-card reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
                  <h3>{tool.title}</h3>
                  <p>{tool.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}
        <PublicFooter />
      </div>
    </>
  );
};

export default PricingPage;
